// Role interview question pages at /interview-questions/<slug>.
// Each role lives in src/data/roles/<slug>.ts; this file collects them and turns each into the
// TemplateDoc shape that the template page renders, so roles and templates share one layout.

import { HEALTH_AVOID, PROTECTED_AVOID, RATING_SCALE, TEMPLATES_PUBLISHED, type TemplateDoc } from './templates';
import type { RoleQuestionSet, RoleSector } from './roles/types';
import { registeredNurse } from './roles/registered-nurse';
import { certifiedNursingAssistant } from './roles/certified-nursing-assistant';
import { medicalAssistant } from './roles/medical-assistant';
import { pharmacyTechnician } from './roles/pharmacy-technician';
import { caregiver } from './roles/caregiver';
import { administrativeAssistant } from './roles/administrative-assistant';
import { receptionist } from './roles/receptionist';
import { customerServiceRepresentative } from './roles/customer-service-representative';
import { salesRepresentative } from './roles/sales-representative';
import { retailSalesAssociate } from './roles/retail-sales-associate';
import { warehouseAssociate } from './roles/warehouse-associate';
import { softwareEngineer } from './roles/software-engineer';
import { dataAnalyst } from './roles/data-analyst';
import { itSupportSpecialist } from './roles/it-support-specialist';
import { hrManager } from './roles/hr-manager';
import { projectManager } from './roles/project-manager';
import { teacher } from './roles/teacher';
import { dentalAssistant } from './roles/dental-assistant';
import { phlebotomist } from './roles/phlebotomist';
import { executiveAssistant } from './roles/executive-assistant';
import { financialAnalyst } from './roles/financial-analyst';
import { bookkeeper } from './roles/bookkeeper';
import { securityGuard } from './roles/security-guard';
import { electrician } from './roles/electrician';
import { maintenanceTechnician } from './roles/maintenance-technician';
import { productManager } from './roles/product-manager';
import { operationsManager } from './roles/operations-manager';
import { recruiter } from './roles/recruiter';
import { supervisor } from './roles/supervisor';
import { businessAnalyst } from './roles/business-analyst';
import { server } from './roles/server';
import { paraprofessional } from './roles/paraprofessional';
import { bartender } from './roles/bartender';
import { socialWorker } from './roles/social-worker';
import { accountManager } from './roles/account-manager';
import { nursePractitioner } from './roles/nurse-practitioner';
import { pharmacist } from './roles/pharmacist';
import { caseManager } from './roles/case-manager';
import { assistantManager } from './roles/assistant-manager';
import { paralegal } from './roles/paralegal';
import { programManager } from './roles/program-manager';
import { bankTeller } from './roles/bank-teller';
import { dataScientist } from './roles/data-scientist';
import { dataEngineer } from './roles/data-engineer';
import { substituteTeacher } from './roles/substitute-teacher';
import { specialEducationTeacher } from './roles/special-education-teacher';
import { assistantPrincipal } from './roles/assistant-principal';
import { preschoolTeacher } from './roles/preschool-teacher';
import { nurseManager } from './roles/nurse-manager';
import { emt } from './roles/emt';
import { registeredBehaviorTechnician } from './roles/registered-behavior-technician';
import { accountsPayableSpecialist } from './roles/accounts-payable-specialist';
import { barista } from './roles/barista';
import { housekeeper } from './roles/housekeeper';
import { custodian } from './roles/custodian';
import { hvacTechnician } from './roles/hvac-technician';
import { principal } from './roles/principal';
import { librarian } from './roles/librarian';
import { devopsEngineer } from './roles/devops-engineer';
import { scrumMaster } from './roles/scrum-master';
import { restaurantHost } from './roles/restaurant-host';
import { icuNurse } from './roles/icu-nurse';
import { storeManager } from './roles/store-manager';
import { eventCoordinator } from './roles/event-coordinator';
import { cybersecurityAnalyst } from './roles/cybersecurity-analyst';
import { networkEngineer } from './roles/network-engineer';
import { physicalTherapist } from './roles/physical-therapist';
import { cashier } from './roles/cashier';
import { licensedPracticalNurse } from './roles/licensed-practical-nurse';
import { officeManager } from './roles/office-manager';
import { cook } from './roles/cook';
import { accountant } from './roles/accountant';

const SITE = 'https://beatview.ai';

export const ROLE_SETS: RoleQuestionSet[] = [
  registeredNurse,
  certifiedNursingAssistant,
  medicalAssistant,
  pharmacyTechnician,
  caregiver,
  administrativeAssistant,
  receptionist,
  customerServiceRepresentative,
  salesRepresentative,
  retailSalesAssociate,
  warehouseAssociate,
  softwareEngineer,
  dataAnalyst,
  itSupportSpecialist,
  hrManager,
  projectManager,
  teacher,
  dentalAssistant,
  phlebotomist,
  executiveAssistant,
  financialAnalyst,
  bookkeeper,
  securityGuard,
  electrician,
  maintenanceTechnician,
  productManager,
  operationsManager,
  recruiter,
  supervisor,
  businessAnalyst,
  server,
  paraprofessional,
  bartender,
  socialWorker,
  accountManager,
  nursePractitioner,
  pharmacist,
  caseManager,
  assistantManager,
  paralegal,
  programManager,
  bankTeller,
  dataScientist,
  dataEngineer,
  substituteTeacher,
  specialEducationTeacher,
  assistantPrincipal,
  preschoolTeacher,
  nurseManager,
  emt,
  registeredBehaviorTechnician,
  accountsPayableSpecialist,
  barista,
  housekeeper,
  custodian,
  hvacTechnician,
  principal,
  librarian,
  devopsEngineer,
  scrumMaster,
  restaurantHost,
  icuNurse,
  storeManager,
  eventCoordinator,
  cybersecurityAnalyst,
  networkEngineer,
  physicalTherapist,
  cashier,
  licensedPracticalNurse,
  officeManager,
  cook,
  accountant,
];

export const ROLE_SECTORS: RoleSector[] = ['Healthcare', 'Education', 'Office and administration', 'Social services', 'Sales and customer service', 'Hospitality and food service', 'Finance and accounting', 'Warehouse and logistics', 'Facilities, trades and security', 'Technology and data', 'Management and HR'];

export const INTERVIEW_QUESTIONS_PATH = '/interview-questions';

export const roleUrl = (slug?: string) => (slug ? `${SITE}${INTERVIEW_QUESTIONS_PATH}/${slug}` : `${SITE}${INTERVIEW_QUESTIONS_PATH}`);

export const roleQuestionCount = (r: RoleQuestionSet) => r.groups.reduce((n, g) => n + g.questions.length, 0);

/** "Nurse interview questions" → "nurse interview questions"; acronyms and names such as "CNA", "HR" or "DevOps" keep their case. */
const lowerFirst = (s: string) => (/[A-Z]/.test(s.split(' ')[0].slice(1)) ? s : s.charAt(0).toLowerCase() + s.slice(1));

/** Title case for title tags, keeping acronyms. */
const titleCase = (s: string) => s.split(' ').map(w => (/^[A-Z]{2,}/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');

/** "Nurse Interview Questions (22 + Free Scorecard)", shortened for long role names so results don't cut it off. */
const seoTitle = (keyword: string, count: number) => {
  const full = `${titleCase(keyword)} (${count} + Free Scorecard)`;
  return full.length <= 60 ? full : `${titleCase(keyword)} (${count} + Scorecard)`;
};

export function roleToTemplate(r: RoleQuestionSet): TemplateDoc {
  const count = roleQuestionCount(r);
  // The next two roles in the same sector, wrapping round, so every role page gets links from its neighbors.
  const sector = ROLE_SETS.filter(o => o.sector === r.sector);
  const at = sector.findIndex(o => o.slug === r.slug);
  const peers = [1, 2].map(k => sector[(at + k) % sector.length]).filter((o, i, all) => o.slug !== r.slug && all.findIndex(x => x.slug === o.slug) === i);
  return {
    slug: r.slug,
    name: r.keyword,
    category: 'Interviewing',
    h1: [`${r.keyword}:`, `${count} questions and a scorecard for interviewers`],
    seoTitle: seoTitle(r.keyword, count),
    description: r.description,
    shortAnswer: r.shortAnswer,
    published: r.published ?? TEMPLATES_PUBLISHED,
    updated: r.published ?? TEMPLATES_PUBLISHED,
    file: { href: `/resources/${r.slug}-interview-questions.csv`, format: 'CSV', summary: `All ${count} questions with why you ask each one, what a strong answer shows and follow-ups, plus the ${r.role.toLowerCase()} scorecard and rating guide.` },
    whenToUse: r.context,
    groupsHeading: { title: `${count} ${lowerFirst(r.keyword)}`, description: 'Grouped by what they test. Pick the questions that match the role, ask every candidate the same ones in the same order, and score each answer against the scorecard below.' },
    groups: r.groups,
    rubric: { title: `${r.role} interview scorecard`, description: 'Five criteria for this role, with what a score of 1, 3 and 5 looks like. Scores of 2 and 4 sit between them.', rows: r.rubric },
    scale: { title: 'The 1–5 rating scale', description: 'The same scale for every criterion and every candidate.', levels: RATING_SCALE },
    steps: [
      { title: 'Agree the must-haves first', body: 'Confirm the essential credentials, experience and availability with the hiring manager or client before any interviews.' },
      { title: 'Pick 8 to 12 questions', body: 'Take the must-have questions, then the questions that test what this role needs most. Use the same set, in the same order, for every candidate.' },
      { title: 'Ask for real examples', body: 'When you hear “we” or “I would”, ask what the candidate personally did, and what happened in the end.' },
      { title: 'Score before you discuss', body: 'Rate each criterion on the scorecard with the evidence behind it, then compare with other interviewers.' },
      { title: 'Verify before you submit', body: 'Check licenses, certifications and right to work against the original source before you put the candidate forward.' },
    ],
    avoid: { title: 'Red flags, and questions not to ask', items: [...r.redFlags, PROTECTED_AVOID, HEALTH_AVOID] },
    beatview: { title: `Screen ${lowerFirst(r.role)} applicants before the first call`, body: 'Add these questions to a Beatview AI interview and every applicant answers them on video or audio, with the same time limit. Beatview scores each answer against your criteria and shows the reasoning, and you can share the shortlist with your client through a password-protected link. AI interviews are on the Pro plan; the Free plan screens resumes for one active job.' },
    faqs: r.faqs,
    related: {
      slugs: [],
      links: [
        ...peers.map(p => ({ title: p.keyword, body: `Questions and a scorecard for ${lowerFirst(p.role)} interviews.`, href: `${INTERVIEW_QUESTIONS_PATH}/${p.slug}` })),
        { title: 'Interview rubric template', body: 'Write anchored 1–5 scores for any role.', href: '/templates/interview-rubric' },
        { title: 'Reference check questions', body: '25 questions to ask a candidate’s former manager.', href: '/templates/reference-check-questions' },
      ],
    },
    sources: r.sources,
  };
}

export const ROLE_TEMPLATES: TemplateDoc[] = ROLE_SETS.map(roleToTemplate);

export const getRoleSet = (slug: string) => ROLE_SETS.find(r => r.slug === slug);
export const getRoleTemplate = (slug: string) => ROLE_TEMPLATES.find(t => t.slug === slug);

export const INTERVIEW_QUESTIONS_HUB = {
  title: 'Interview Questions by Role: Free Question Sets and Scorecards',
  description: 'Free interview questions by role for recruiters and hiring managers, with what a strong answer shows, credential checks and a downloadable scorecard for each.',
  shortAnswer: 'Role-specific interview questions for recruiters, agency consultants and hiring managers. Each set has 20 to 25 questions grouped by what they test, what a strong answer shows, follow-up probes, the must-have checks for the role, red flags, a five-criterion scorecard and a free CSV download. Ask every candidate for a role the same questions in the same order, and score answers against the scorecard before you discuss them.',
};
